import { Grid } from '@material-ui/core';
import BackspaceIcon from '@material-ui/icons/Backspace';
import { Keyboard } from '@ncr-design-system/keyboard-react';
import React from 'react';
import {
  onManualWeightClick,
  onQuantityClick,
} from 'src/sales-keypad/business-logic/handle-sales-keypad-actions';
import { ISalesKeypadModifiers } from 'src/sales-keypad/components/sales-keypad';
import CustomKeypadButton from '../custom-keypad-button';
import FlexibleText from '../flexible-text';
import { styles } from './numpad-styles';

interface INumpadProps {
  keyboardId: string;
  minHeight?: string;
  dataTestIdPrefix?: string;
  disableManualWeight?: boolean;
  disableQuantity?: boolean;
  disableDecimalInput?: boolean;
  replaceDecimalInput?: boolean;
  currentInput: string;
  setItemCode: (value: string) => void;
  salesKeypadModifiers?: ISalesKeypadModifiers;
}

const Numpad: React.FC<INumpadProps> = ({
  disableManualWeight,
  disableQuantity,
  disableDecimalInput,
  keyboardId,
  dataTestIdPrefix,
  currentInput,
  setItemCode,
  salesKeypadModifiers,
  replaceDecimalInput,
}: INumpadProps): JSX.Element => {
  const classes = styles();

  return (
    <Grid container direction='column' className={classes.container}>
      <Keyboard className='fullHeight' global={false} translate='yes' owns={keyboardId}>
        <Grid className={`${classes.spacingOffset} fullHeight`} spacing={1} container>
          <Grid spacing={1} container item>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='7'
                buttonName='7'
                data-testid={`${dataTestIdPrefix}seven-button`}
              >
                7
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='8'
                buttonName='8'
                data-testid={`${dataTestIdPrefix}eight-button`}
              >
                8
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='9'
                buttonName='9'
                data-testid={`${dataTestIdPrefix}nine-button`}
              >
                9
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                nonNumerical
                translate='yes'
                pressAction='delete'
                // @ts-ignore
                deleteValue='all'
                buttonName='C'
                data-testid={`${dataTestIdPrefix}clear-button`}
              >
                C
              </CustomKeypadButton>
            </BasicKeypadGridItem>
          </Grid>
          <Grid spacing={1} container item>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='4'
                buttonName='4'
                data-testid={`${dataTestIdPrefix}four-button`}
              >
                4
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='5'
                buttonName='5'
                data-testid={`${dataTestIdPrefix}five-button`}
              >
                5
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='6'
                buttonName='6'
                data-testid={`${dataTestIdPrefix}six-button`}
              >
                6
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                disabled={disableManualWeight}
                nonNumerical
                onClick={() => {
                  const newKeypadData = onManualWeightClick(currentInput);
                  setItemCode('');
                  if (salesKeypadModifiers) {
                    salesKeypadModifiers.setManualWeight?.(newKeypadData.isManualWeight);
                    salesKeypadModifiers.setMetricValue?.(newKeypadData.metricValue);
                    salesKeypadModifiers.setType?.(newKeypadData.type);
                    salesKeypadModifiers.setQuantityEntered?.(newKeypadData.quantityEntered);
                  }
                }}
                translate='yes'
                pressAction='none'
                buttonName='MW'
                data-testid={`${dataTestIdPrefix}manual-weight-button`}
              >
                <FlexibleText>MW</FlexibleText>
              </CustomKeypadButton>
            </BasicKeypadGridItem>
          </Grid>

          <Grid spacing={1} container item>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='1'
                buttonName='1'
                data-testid={`${dataTestIdPrefix}one-button`}
              >
                1
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='2'
                buttonName='2'
                data-testid={`${dataTestIdPrefix}two-button`}
              >
                2
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='3'
                buttonName='3'
                data-testid={`${dataTestIdPrefix}three-button`}
              >
                3
              </CustomKeypadButton>
            </BasicKeypadGridItem>
            <BasicKeypadGridItem>
              <CustomKeypadButton
                nonNumerical
                onClick={() => {
                  const newKeypadData = onQuantityClick(currentInput);
                  setItemCode('');
                  if (salesKeypadModifiers && newKeypadData) {
                    salesKeypadModifiers.setManualWeight?.(newKeypadData.isManualWeight);
                    salesKeypadModifiers.setMetricValue?.(newKeypadData.metricValue);
                    salesKeypadModifiers.setType?.(newKeypadData.type);
                    salesKeypadModifiers.setQuantityEntered?.(newKeypadData.quantityEntered);
                  }
                }}
                translate='yes'
                pressAction='none'
                buttonName='QTY'
                disabled={disableQuantity}
                data-testid={`${dataTestIdPrefix}quantity-button`}
              >
                <FlexibleText>QTY</FlexibleText>
              </CustomKeypadButton>
            </BasicKeypadGridItem>
          </Grid>
          <Grid spacing={1} container item>
            <Grid xs={6} item>
              <CustomKeypadButton
                translate='yes'
                pressAction='add'
                addValue='0'
                buttonName='0'
                data-testid={`${dataTestIdPrefix}zero-button`}
                doubleWidth
              >
                0
              </CustomKeypadButton>
            </Grid>
            {!replaceDecimalInput ? (
              <BasicKeypadGridItem data-testid={`decimal-grid`}>
                <CustomKeypadButton
                  nonNumerical
                  translate='yes'
                  pressAction='add'
                  addValue='.'
                  buttonName='.'
                  disabled={disableDecimalInput}
                  data-testid={`${dataTestIdPrefix}decimal-button`}
                >
                  .
                </CustomKeypadButton>
              </BasicKeypadGridItem>
            ) : (
              <BasicKeypadGridItem data-testid={`double-0-grid`}>
                <CustomKeypadButton
                  translate='yes'
                  pressAction='add'
                  addValue='00'
                  buttonName='00'
                  data-testid={`${dataTestIdPrefix}double-0-button`}
                >
                  00
                </CustomKeypadButton>
              </BasicKeypadGridItem>
            )}
            <BasicKeypadGridItem>
              <CustomKeypadButton
                nonNumerical
                translate='yes'
                pressAction='delete'
                deleteValue={1}
                buttonName='X'
                data-testid={`${dataTestIdPrefix}delete-button`}
              >
                <BackspaceIcon className={classes.icon} />
              </CustomKeypadButton>
            </BasicKeypadGridItem>
          </Grid>
        </Grid>
      </Keyboard>
    </Grid>
  );
};

const BasicKeypadGridItem: React.FC = ({ children }): JSX.Element => {
  return (
    <Grid item xs={3}>
      {children}
    </Grid>
  );
};

export default Numpad;
